'use client'

import { useTranslations } from 'next-intl'
import { RefObject } from 'react'

import { Card } from '@/entities/contract'
import { useDialog } from '@/shared/lib'
import { AlertDialog, SegmentedControl } from '@/shared/ui'

import { AccountPayment, Props as AccountPaymentProps } from './account'
import { CardPayment, Props as CardPaymentProps } from './card'
import { PaymentMethod, usePaymentMethod } from '../../model/payment'

const LIMIT_OF_PAY_FEE_WITH_CARD = 10000000

const DIALOG = 'PAYMENT_SUGGESTION'

export type Props = AccountPaymentProps &
  CardPaymentProps & {
    ref: RefObject<HTMLDivElement | null>
  }

export const Payment = ({
  payDeadlineTime,
  vaBankName,
  vaAccountHolder,
  vaAccount,
  roomName,
  startDate,
  endDate,
  finalPayFee,
  ref,
}: Props) => {
  const t = useTranslations()
  const canPayWithVirtualAccountOnly = finalPayFee >= LIMIT_OF_PAY_FEE_WITH_CARD

  const { paymentMethod, isSelected, updatePaymentMethod } = usePaymentMethod()

  const { isOpen, open, close } = useDialog()

  const handlePaymentMethodChange = (value: string) => {
    if (canPayWithVirtualAccountOnly && value === PaymentMethod.CARD) {
      open(DIALOG)

      return
    }

    updatePaymentMethod(value)
  }

  const handleDialogClose = () => {
    close(DIALOG)
  }

  return (
    <Card.Root spacing="large" ref={ref}>
      <Card.Item>
        <Card.ItemTitle>{t('lease_detail.make_payment.title')}</Card.ItemTitle>
        <Card.ItemContent className="space-y-6">
          <SegmentedControl.Root
            size="medium"
            type="single"
            value={paymentMethod}
            onValueChange={handlePaymentMethodChange}
          >
            <SegmentedControl.Item value={PaymentMethod.ACCOUNT}>
              {t('lease_detail.make_payment.button.method.bank_transfer')}
            </SegmentedControl.Item>
            <SegmentedControl.Item value={PaymentMethod.CARD}>
              {t('lease_detail.make_payment.button.method.card')}
            </SegmentedControl.Item>
          </SegmentedControl.Root>

          {isSelected(PaymentMethod.ACCOUNT) && (
            <AccountPayment
              payDeadlineTime={payDeadlineTime}
              vaBankName={vaBankName}
              vaAccountHolder={vaAccountHolder}
              vaAccount={vaAccount}
              finalPayFee={finalPayFee}
            />
          )}

          {isSelected(PaymentMethod.CARD) && (
            <CardPayment
              payDeadlineTime={payDeadlineTime}
              finalPayFee={finalPayFee}
              roomName={roomName}
              startDate={startDate}
              endDate={endDate}
            />
          )}

          <AlertDialog
            isOpen={isOpen(DIALOG)}
            onOpenChange={handleDialogClose}
            title={t('card_payment.dialog.card_payment_notice.title')}
            content={t('card_payment.dialog.card_payment_notice.description')}
          />
        </Card.ItemContent>
      </Card.Item>
    </Card.Root>
  )
}
